import type { ContractBlock, ContractDocument, ContractMarkType } from "@closer/shared";

/**
 * KANONİK BELGE ⇄ EDİTÖR BELGESİ (CLOS-118'in 20 Ağustos kanonik kararı).
 *
 * ### ⚠ Neden bu köprü var
 *
 * Kanonik saklama biçimi yapısal ve öyle kalıyor (ADR-0026): `{ clauses: [{ id, title,
 * blocks }] }`. Kartın istediği şey ise saklama biçimi değil YAZIM YÜZEYİ: *"belge/sayfa
 * odaklı ... beyaz sayfa ... başlık, paragraf, kalın/italik, liste, hizalama"*. İkisi
 * çelişmiyor; bu modül aradaki çeviriyi yapıyor.
 *
 * Daha önce aynı köprünün DÜZ METİN sürümü vardı (`belgeMetni.ts`) ve o da duruyor:
 * "Maddeler" yüzeyi ve şablon ekranları onu kullanıyor. Bu modül onun yerine geçmiyor,
 * yanına geliyor — biçimlendirmeyi düz metin taşıyamaz.
 *
 * ### ⚠ MADDE SINIRINI `clauseId` ÖZNİTELİĞİ BELİRLİYOR, BAŞLIK SEVİYESİ DEĞİL
 *
 * İlk tasarımda madde başlığını "1. seviye başlık" diye ayırt edecektim. Ama artık madde
 * İÇİNDE de `heading` bloğu olabiliyor (şema öyle diyor, DOCX içe aktarımı da öyle üretir).
 * Seviyeye baksaydık, içe aktarılan bir belgedeki 1. seviye ara başlık sessizce YENİ BİR
 * MADDE doğururdu ve kullanıcı sözleşmesinin madde sayısının değiştiğini ancak sonradan
 * görürdü. Öznitelik seviyeden bağımsız ve tek anlamlı.
 *
 * ### ⚠ GİDİŞ-DÖNÜŞ KAYIPSIZ olmak zorunda
 *
 * Ekran belgeyi editöre çeviriyor, kullanıcı düzenliyor, geri çeviriyoruz. Kayıplı olsaydı
 * hiç dokunulmamış bir madde bile her açılışta biraz değişir ve `[095]` karşılaştırması
 * olmayan bir fark gösterirdi. Testler iki yönü de ölçüyor.
 *
 * ### ⚠ `text` HÂLÂ DÜZ METİN
 *
 * İşaretler ofsetle taşınıyor ve `text`'in üstüne biniyor. Müzakere (`targetBlockIndex` +
 * `currentText`), karşılaştırma ve dışa aktarma `text` okumaya devam ediyor.
 */

/** Editörün ProseMirror belgesi — yalnız bu modülün ürettiği/okuduğu şekil. */
export type EditorDugumu = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: EditorDugumu[];
  text?: string;
  marks?: { type: string }[];
};
export type EditorBelgesi = { type: "doc"; content: EditorDugumu[] };

/** Şemadaki blok türü ⇄ editördeki liste kabı. */
const LISTE_KABI: Record<"bullet" | "ordered", string> = {
  bullet: "bulletList",
  ordered: "orderedList",
};

/**
 * Bir bloğun metnini işaretlere göre parçalara böler.
 *
 * ⚠ Sınırlar İŞARET UÇLARINDAN toplanıyor, işaret başına parça üretilmiyor: iç içe geçen
 * kalın+italik aralıkları ancak böyle doğru çıkıyor. Her işareti kendi parçası yapsaydık
 * çakışan aralıklarda metin iki kez yazılırdı.
 */
function metniParcala(blok: ContractBlock): EditorDugumu[] {
  const isaretler = blok.marks ?? [];
  if (isaretler.length === 0) return blok.text ? [{ type: "text", text: blok.text }] : [];

  const sinirlar = new Set<number>([0, blok.text.length]);
  for (const isaret of isaretler) {
    sinirlar.add(Math.max(0, isaret.start));
    sinirlar.add(Math.min(blok.text.length, isaret.end));
  }
  const siraliSinirlar = [...sinirlar].sort((a, b) => a - b);

  const parcalar: EditorDugumu[] = [];
  for (let i = 0; i < siraliSinirlar.length - 1; i += 1) {
    const bas = siraliSinirlar[i]!;
    const son = siraliSinirlar[i + 1]!;
    const metin = blok.text.slice(bas, son);
    if (!metin) continue;

    const turler = isaretler
      .filter((isaret) => isaret.start <= bas && isaret.end >= son)
      .map((isaret) => isaret.type);
    const benzersiz = [...new Set(turler)];

    parcalar.push({
      type: "text",
      text: metin,
      ...(benzersiz.length > 0 ? { marks: benzersiz.map((type) => ({ type })) } : {}),
    });
  }
  return parcalar;
}

/** Metin bloğunu editör düğümüne çevirir (liste kabı ÇAĞIRANIN işi). */
function blokDugumu(blok: ContractBlock): EditorDugumu {
  const icerik = metniParcala(blok);
  const hizalama = blok.align ? { textAlign: blok.align } : {};
  /**
   * ⚠ STABLE BLOCK IDENTITY (CLOS-1233 — Kanon §11). Kimlik düğümün ÖZNİTELİĞİNDE
   * taşınıyor, dizideki konumunda değil: konum kullanıcı yazdıkça değişen bir şey.
   */
  const kimlik = blok.id ? { blockId: blok.id } : {};

  if (blok.kind === "heading") {
    return {
      type: "heading",
      /** ⚠ `clauseId: null` = madde İÇİ başlık; sınır değil. Ayrım tek yerde, burada. */
      attrs: { level: blok.level ?? 2, clauseId: null, ...kimlik, ...hizalama },
      content: icerik,
    };
  }
  return { type: "paragraph", attrs: { ...kimlik, ...hizalama }, content: icerik };
}

export function belgeyiEditoreCevir(belge: ContractDocument): EditorBelgesi {
  const content: EditorDugumu[] = [];

  for (const madde of belge.clauses) {
    content.push({
      type: "heading",
      attrs: { level: 1, clauseId: madde.id },
      content: madde.title ? [{ type: "text", text: madde.title }] : [],
    });

    /**
     * ⚠ ARDIŞIK liste blokları TEK kaba giriyor. Her bloğu kendi kabına koysaydık,
     * üç maddelik bir liste ekranda üç ayrı tek maddelik liste gibi görünür ve
     * numaralandırma her satırda 1'den başlardı.
     */
    let i = 0;
    while (i < madde.blocks.length) {
      const blok = madde.blocks[i]!;
      if (blok.kind === "bullet" || blok.kind === "ordered") {
        const tur = blok.kind;
        const ogeler: EditorDugumu[] = [];
        while (i < madde.blocks.length && madde.blocks[i]!.kind === tur) {
          ogeler.push({ type: "listItem", content: [blokDugumu(madde.blocks[i]!)] });
          i += 1;
        }
        content.push({ type: LISTE_KABI[tur], content: ogeler });
        continue;
      }
      content.push(blokDugumu(blok));
      i += 1;
    }
  }

  return { type: "doc", content };
}

/** Bir metin düğümü dizisini düz metne ve ofset işaretlerine indirger. */
function icerigiCoz(icerik: EditorDugumu[] | undefined): Pick<ContractBlock, "text" | "marks"> {
  let text = "";
  const marks: NonNullable<ContractBlock["marks"]> = [];

  for (const dugum of icerik ?? []) {
    const parca = dugum.text ?? "";
    if (!parca) continue;
    const bas = text.length;
    text += parca;

    for (const isaret of dugum.marks ?? []) {
      const tur = isaret.type as ContractMarkType;
      /**
       * ⚠ BİTİŞİK aynı türdeki işaretler BİRLEŞTİRİLİYOR. Birleştirmeseydik "kalın bir
       * cümle" üç parçaya bölündüğünde üç ayrı işaret doğar, gidiş-dönüş sonrası belge
       * kendisine eşit çıkmaz ve karşılaştırma olmayan bir fark gösterirdi.
       */
      const sonuncu = marks[marks.length - 1];
      if (sonuncu && sonuncu.type === tur && sonuncu.end === bas) {
        sonuncu.end = text.length;
        continue;
      }
      marks.push({ type: tur, start: bas, end: text.length });
    }
  }

  return { text, ...(marks.length > 0 ? { marks } : {}) };
}

function hizalamaOku(dugum: EditorDugumu): Pick<ContractBlock, "align"> {
  const deger = dugum.attrs?.["textAlign"];
  /** ⚠ "left" YAZILMIYOR: varsayılanı alan olarak saklamak her bloğa gürültü eklerdi. */
  if (deger === "center" || deger === "right" || deger === "justify") return { align: deger };
  return {};
}

/**
 * ⚠ KİMLİK ÖZNİTELİKTEN OKUNUYOR, KONUMDAN DEĞİL (CLOS-1233 — Kanon §11).
 *
 * Bu fonksiyon eskiden `kimlik(index)` alıyordu ve ekran ona `kimlikler.current[i]` —
 * yani SIRAYA göre dizilmiş bir dizi — veriyordu. Sonuç kanonun açıkça yasakladığı
 * davranıştı: öne bir madde eklendiğinde alttaki her madde bir öncekinin kimliğini
 * devralıyordu ve o maddeye asılı her yorum, müzakere ve revizyon sessizce başka bir
 * metnin altına düşüyordu. Ölçüldü, testle sabitlendi, düzeltildi.
 *
 * `uret` artık yalnız GERÇEKTEN YENİ düğümler için çağrılıyor.
 */
function ozniteliktenKimlik(attrs: Record<string, unknown> | undefined, anahtar: string) {
  const deger = attrs?.[anahtar];
  return typeof deger === "string" && deger.length > 0 ? deger : undefined;
}

export function editordenBelgeyeCevir(
  belgeJson: EditorBelgesi,
  uret: () => string,
): ContractDocument {
  const clauses: ContractDocument["clauses"] = [];

  /**
   * ⚠ İLK MADDE BAŞLIKSIZ DOĞABİLİR. Kullanıcı beyaz sayfaya doğrudan yazmaya başlarsa
   * ilk düğüm bir paragraf olur; madde açmasaydık o metin sessizce KAYBOLURDU.
   */
  const maddeAc = () => {
    clauses.push({ id: uret(), title: "", blocks: [] });
    return clauses[clauses.length - 1]!;
  };
  let aktif: ContractDocument["clauses"][number] | null = null;

  for (const dugum of belgeJson.content ?? []) {
    if (dugum.type === "heading" && dugum.attrs?.["clauseId"] !== undefined) {
      const clauseId = dugum.attrs["clauseId"];
      if (clauseId !== null) {
        const { text } = icerigiCoz(dugum.content);
        clauses.push({ id: String(clauseId), title: text, blocks: [] });
        aktif = clauses[clauses.length - 1]!;
        continue;
      }
    }

    aktif ??= maddeAc();

    if (dugum.type === "bulletList" || dugum.type === "orderedList") {
      const tur = dugum.type === "bulletList" ? "bullet" : "ordered";
      for (const oge of dugum.content ?? []) {
        for (const ic of oge.content ?? []) {
          const { text, marks } = icerigiCoz(ic.content);
          if (!text) continue;
          aktif.blocks.push({
            kind: tur,
            text,
            id: ozniteliktenKimlik(ic.attrs, "blockId") ?? uret(),
            ...(marks ? { marks } : {}),
            ...hizalamaOku(ic),
          });
        }
      }
      continue;
    }

    const { text, marks } = icerigiCoz(dugum.content);
    if (!text) continue;

    if (dugum.type === "heading") {
      const seviye = Number(dugum.attrs?.["level"] ?? 2);
      aktif.blocks.push({
        kind: "heading",
        text,
        id: ozniteliktenKimlik(dugum.attrs, "blockId") ?? uret(),
        level: Number.isFinite(seviye) ? Math.min(3, Math.max(1, seviye)) : 2,
        ...(marks ? { marks } : {}),
        ...hizalamaOku(dugum),
      });
      continue;
    }

    aktif.blocks.push({
      kind: "paragraph",
      text,
      id: ozniteliktenKimlik(dugum.attrs, "blockId") ?? uret(),
      ...(marks ? { marks } : {}),
      ...hizalamaOku(dugum),
    });
  }

  /**
   * ⚠ Başlığı da gövdesi de boş olan madde ATILIYOR. Kullanıcı bir maddeyi tamamen
   * silince editörde boş bir başlık düğümü kalabiliyor; onu kaydetseydik sözleşmede
   * görünmez bir madde birikirdi.
   */
  return { clauses: clauses.filter((madde) => madde.title !== "" || madde.blocks.length > 0) };
}
